/** Pure helpers for the OAuth 2.0 Authorization Code + PKCE flow against the Cognito Hosted UI. */

export function base64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Cryptographically random URL-safe string (used for code_verifier and state). */
export function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/** Accepts "xxx.auth.region.amazoncognito.com" or a full https:// origin. */
export function cognitoOrigin(domain: string): string {
  const d = domain.trim().replace(/\/+$/, '');
  return /^https?:\/\//.test(d) ? d : `https://${d}`;
}

export interface AuthorizeParams {
  clientId: string;
  redirectUri: string;
  challenge: string;
  state: string;
}

export function buildAuthorizeUrl(cognitoDomain: string, p: AuthorizeParams): string {
  const q = new URLSearchParams({
    response_type: 'code',
    client_id: p.clientId,
    redirect_uri: p.redirectUri,
    scope: 'openid email profile',
    code_challenge: p.challenge,
    code_challenge_method: 'S256',
    state: p.state,
  });
  return `${cognitoOrigin(cognitoDomain)}/oauth2/authorize?${q.toString()}`;
}

export function buildLogoutUrl(cognitoDomain: string, clientId: string, logoutUri: string): string {
  const q = new URLSearchParams({ client_id: clientId, logout_uri: logoutUri });
  return `${cognitoOrigin(cognitoDomain)}/logout?${q.toString()}`;
}

export type CallbackResult = { ok: true; code: string } | { ok: false; reason: string };

/** Validates the redirect back from Cognito: error param, missing code, and the CSRF `state`. */
export function parseCallback(search: string, expectedState: string | null): CallbackResult {
  const p = new URLSearchParams(search);
  const error = p.get('error');
  if (error) return { ok: false, reason: p.get('error_description') ?? error };
  const code = p.get('code');
  const state = p.get('state');
  if (!code) return { ok: false, reason: 'Missing authorization code' };
  if (!expectedState || !state || state !== expectedState) return { ok: false, reason: 'Invalid sign-in state (possible CSRF); please try again' };
  return { ok: true, code };
}

export function tokenRequestBody(params: Record<string, string>): string {
  return new URLSearchParams(params).toString();
}
